import { useState, useRef, useEffect } from 'react';
import { useLanguage } from '@/hooks/useLanguage';
import { FadeInView } from '@/components/animations/FadeInView';
import { Modal } from '@/components/ui/Modal';
import { translations } from '@/i18n/translations';

interface HeroSectionProps {
  className?: string;
}

export function HeroSection({ className = '' }: HeroSectionProps) {
  const { language } = useLanguage();
  const tr = translations[language];
  const hero = tr.hero;

  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const chromeStoreUrl = 'https://chromewebstore.google.com/detail/page-picker/jknigpbnkkmohofdfnangfffbjbomenn';
  const edgeStoreUrl = 'https://microsoftedge.microsoft.com/addons/detail/page-picker/aiglimalfnljcondcpinnbpakojheglj';

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <section
      className={`relative py-20 md:py-32 bg-gradient-to-b from-orange-50 to-white overflow-hidden ${className}`}
    >
      {/* 背景装饰 */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-primary-200 rounded-full opacity-20 blur-3xl" />
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-primary-300 rounded-full opacity-20 blur-3xl" />
      </div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative">
        <FadeInView className="max-w-4xl mx-auto text-center">
          <div className="space-y-8">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 leading-tight">
              {hero.title}
            </h1>

            <p className="text-lg sm:text-xl lg:text-2xl text-gray-600 leading-relaxed max-w-2xl mx-auto">
              {hero.subtitle}
            </p>

            <div className="flex flex-wrap justify-center gap-3 pt-2">
              {hero.badges.map((badge, index) => (
                <span
                  key={index}
                  className="inline-flex items-center px-4 py-2 bg-white border-2 border-primary-500 text-primary-600 rounded-full text-sm font-medium shadow-sm"
                >
                  {badge}
                </span>
              ))}
            </div>

            {/* CTA 按钮组 */}
            <div className="pt-4 flex flex-col sm:flex-row gap-4 justify-center items-center">
              {/* 应用商店下拉按钮 */}
              <div className="relative" ref={dropdownRef}>
                <button
                  onClick={() => setIsDropdownOpen((v) => !v)}
                  className="inline-flex items-center justify-center gap-2 px-8 py-4 text-lg font-medium rounded-lg bg-primary-500 text-white hover:bg-primary-600 hover:scale-105 shadow-xl transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                >
                  {hero.cta}
                  <svg
                    className={`w-5 h-5 transition-transform duration-200 ${isDropdownOpen ? 'rotate-180' : ''}`}
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                  </svg>
                </button>

                {isDropdownOpen && (
                  <div className="absolute top-full left-0 mt-2 w-56 bg-white rounded-xl shadow-2xl border border-gray-100 overflow-hidden z-20">
                    {/* Chrome 应用商店 */}
                    <a
                      href={chromeStoreUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => setIsDropdownOpen(false)}
                      className="flex items-center gap-3 px-4 py-3 text-gray-800 hover:bg-primary-50 hover:text-primary-600 transition-colors"
                    >
                      <img src="/chrome-icon.svg" alt="" className="w-5 h-5" onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
                      <span className="font-medium">{hero.chromeStore}</span>
                    </a>

                    {/* Edge 应用商店 */}
                    <a
                      href={edgeStoreUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => setIsDropdownOpen(false)}
                      className="flex items-center gap-3 px-4 py-3 text-gray-800 hover:bg-primary-50 hover:text-primary-600 transition-colors border-t border-gray-50"
                    >
                      <img src="/edge-icon.svg" alt="" className="w-5 h-5" onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
                      <span className="font-medium">{hero.edgeStore}</span>
                    </a>
                  </div>
                )}
              </div>

              {/* 直接下载按钮暂时隐藏，后续开放 */}
            </div>
          </div>
        </FadeInView>
      </div>

      {/* 安装说明 Modal */}
      <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} className="w-full max-w-md">
        <div className="p-6">
          <div className="flex items-center justify-between mb-5">
            <div>
                <h2 className="text-xl font-bold text-gray-900">{hero.installGuideTitle}</h2>
                <p className="text-sm text-gray-500 mt-1">{hero.installGuideNote}</p>
              </div>
            <button
              onClick={() => setIsModalOpen(false)}
              className="text-gray-400 hover:text-gray-600 transition-colors"
              aria-label="关闭"
            >
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <ol className="space-y-3">
            {hero.installGuideSteps.map((step, index) => (
              <li key={index} className="flex gap-3">
                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary-500 text-white text-sm font-bold flex items-center justify-center">
                  {index + 1}
                </span>
                <span className="text-gray-700 leading-relaxed">{step}</span>
              </li>
            ))}
          </ol>

          <button
            onClick={() => setIsModalOpen(false)}
            className="mt-6 w-full py-3 rounded-lg bg-primary-500 text-white font-medium hover:bg-primary-600 transition-colors"
          >
            {language === 'zh' ? '知道了' : 'Got it'}
          </button>
        </div>
      </Modal>
    </section>
  );
}
